import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { Order, OrderFilter, OrderUploadResult } from '../../types/order';
import { parseOrderFile, validateOrderFileData } from '../../utils/fileParser';
import apiClient from '../../utils/apiClient';

interface OrderManagementState {
  orders: Order[];
  selectedOrder: Order | null;
  loading: boolean;
  uploadLoading: boolean;
  error: string | null;
  uploadResult: OrderUploadResult | null;
  pagination: {
    current_page: number;
    total_pages: number;
    total_records: number;
    per_page: number;
  };
  filters: OrderFilter;
}

const initialState: OrderManagementState = {
  orders: [],
  selectedOrder: null,
  loading: false,
  uploadLoading: false,
  error: null,
  uploadResult: null,  pagination: {
    current_page: 1,
    total_pages: 1,
    total_records: 0,
    per_page: 20
  },
  filters: {
    page: 1,
    limit: 20
  }
};



// Async thunks
export const uploadOrdersFile = createAsyncThunk(
  'orderManagement/uploadOrdersFile',
  async (file: File, { rejectWithValue, getState }) => {
    try {
      const state = getState() as any;
      const token = state.auth.token;

      // Parse the actual file
      const parsedFile = await parseOrderFile(file);
      
      if (parsedFile.totalRows === 0) {
        throw new Error('File is empty or contains no valid data');
      }

      // Validate the data structure for the new order format
      const { valid: validData, errors: validationErrors } = validateOrderFileData(parsedFile.data);
      
      if (validData.length === 0 && validationErrors.length > 0) {
        throw new Error(`File validation failed: ${validationErrors[0].error}`);
      }

      // Check if we have a valid authentication token
      if (!token) {
        throw new Error('Authentication required. Please log in to upload orders.');
      }

      // Set the token in API client
      apiClient.setAuthToken(token);

      // Upload file using API client
      const result = await apiClient.uploadOrdersFile(file);
      
      return result;
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

export const fetchOrders = createAsyncThunk(
  'orderManagement/fetchOrders',
  async (filters: OrderFilter, { rejectWithValue, getState }) => {
    try {
      const state = getState() as any;
      const token = state.auth.token;
      
      if (!token) {
        throw new Error('Authentication required');
      }
      
      // Set the token in API client
      apiClient.setAuthToken(token);

      // Fetch orders using API client
      const result = await apiClient.getOrders(filters);
      
      return result;
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

export const fetchOrderById = createAsyncThunk(
  'orderManagement/fetchOrderById',
  async (orderId: number, { rejectWithValue, getState }) => {
    try {
      const state = getState() as any;
      const token = state.auth.token;
      
      if (!token) {
        throw new Error('Authentication required');
      }
      
      // Set the token in API client
      apiClient.setAuthToken(token);

      // Fetch order by ID using API client
      const result = await apiClient.getOrderById(orderId);
      
      return result;
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

export const assignWorkerToOrder = createAsyncThunk(
  'orderManagement/assignWorker',
  async ({ orderId, workerId }: { orderId: number; workerId: number }, { rejectWithValue, getState }) => {
    try {
      const state = getState() as any;
      const token = state.auth.token;

      if (!token) {
        throw new Error('Authentication required');
      }

      // Set the token in API client
      apiClient.setAuthToken(token);      // Assign worker using API client
      const result = await apiClient.assignWorkerToOrder(orderId, workerId);
      
      console.log('Assignment result:', { orderId, workerId, result });
      
      return {
        orderId,
        workerId,
        ...result
      };
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

export const deassignWorkerFromOrder = createAsyncThunk(
  'orderManagement/deassignWorker',
  async (orderId: number, { rejectWithValue, getState }) => {
    try {
      const state = getState() as any;
      const token = state.auth.token;

      // Set the token in API client
      apiClient.setAuthToken(token);

      // Deassign worker using API client
      const result = await apiClient.deassignWorkerFromOrder(orderId);
      
      return { orderId, ...result };
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

export const cancelOrder = createAsyncThunk(
  'orderManagement/cancelOrder',
  async (orderId: number, { rejectWithValue, getState }) => {
    try {
      const state = getState() as any;
      const token = state.auth.token;

      // Set the token in API client
      apiClient.setAuthToken(token);

      // Cancel order using API client
      const result = await apiClient.cancelOrder(orderId);
      
      return { orderId, ...result };
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

export const bulkAssignOrders = createAsyncThunk(
  'orderManagement/bulkAssignOrders',
  async ({ orderIds, workerId }: { orderIds: number[]; workerId: number }, { rejectWithValue, dispatch }) => {
    try {
      const results = await Promise.allSettled(
        orderIds.map(orderId => 
          dispatch(assignWorkerToOrder({ orderId, workerId })).unwrap()
        )
      );

      const successful = results.filter(result => result.status === 'fulfilled').length;
      const failed = results.filter(result => result.status === 'rejected').length;

      return {
        success: true,
        message: `${successful} orders assigned successfully. ${failed > 0 ? `${failed} failed.` : ''}`,
        successful,
        failed
      };
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

export const bulkCancelOrders = createAsyncThunk(
  'orderManagement/bulkCancelOrders',
  async (orderIds: number[], { rejectWithValue, dispatch }) => {
    try {
      const results = await Promise.allSettled(
        orderIds.map(orderId => 
          dispatch(cancelOrder(orderId)).unwrap()
        )
      );

      const successful = results.filter(result => result.status === 'fulfilled').length;
      const failed = results.filter(result => result.status === 'rejected').length;

      return {
        success: true,
        message: `${successful} orders cancelled successfully. ${failed > 0 ? `${failed} failed.` : ''}`,
        successful,
        failed
      };
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

// Order picking actions
export const markItemPicked = createAsyncThunk(
  'orderManagement/markItemPicked',
  async ({ orderId, itemId, location, notes }: { orderId: number; itemId: number; location?: string; notes?: string }, { rejectWithValue, getState }) => {
    try {
      const state = getState() as any;
      const token = state.auth.token;

      // Set the token in API client
      apiClient.setAuthToken(token);

      // Mark item as picked using API client
      const result = await apiClient.markItemPicked(orderId, itemId, location, notes);
      
      return { orderId, itemId, location, notes, ...result };
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

export const completeOrder = createAsyncThunk(
  'orderManagement/completeOrder',
  async (orderId: number, { rejectWithValue, getState }) => {
    try {
      const state = getState() as any;
      const token = state.auth.token;

      // Set the token in API client
      apiClient.setAuthToken(token);

      // Complete order using API client
      const result = await apiClient.completeOrder(orderId);
      
      return { orderId, ...result };
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

export const verifyOrder = createAsyncThunk(
  'orderManagement/verifyOrder',
  async ({ orderId, verificationData }: { orderId: number; verificationData: { verification_notes: string; discrepancies: Array<{ item_id: number; issue: string; resolution: string; }> } }, { rejectWithValue, getState }) => {
    try {
      const state = getState() as any;
      const token = state.auth.token;

      // Set the token in API client
      apiClient.setAuthToken(token);

      // Verify order using API client
      const result = await apiClient.verifyOrder(orderId, verificationData);
      
      return { orderId, ...result };
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

const orderManagementSlice = createSlice({
  name: 'orderManagement',
  initialState,
  reducers: {
    setSelectedOrder: (state, action: PayloadAction<Order | null>) => {
      state.selectedOrder = action.payload;
    },
    setFilters: (state, action: PayloadAction<OrderFilter>) => {
      state.filters = { ...state.filters, ...action.payload };
    },
    clearError: (state) => {
      state.error = null;
    },
    clearUploadResult: (state) => {
      state.uploadResult = null;
    },
    resetState: (state) => {
      state.orders = [];
      state.selectedOrder = null;
      state.error = null;
      state.uploadResult = null;
      state.pagination = initialState.pagination;
      state.filters = initialState.filters;
    }
  },
  extraReducers: (builder) => {
    builder
      // Fetch orders
      .addCase(fetchOrders.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchOrders.fulfilled, (state, action) => {
        state.loading = false;
        state.orders = action.payload.data.orders;
        state.pagination = action.payload.data.pagination;
      })
      .addCase(fetchOrders.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Upload orders file
      .addCase(uploadOrdersFile.pending, (state) => {
        state.uploadLoading = true;
        state.error = null;
        state.uploadResult = null;
      })
      .addCase(uploadOrdersFile.fulfilled, (state, action) => {
        state.uploadLoading = false;
        state.uploadResult = action.payload;
      })      .addCase(uploadOrdersFile.rejected, (state, action) => {
        state.uploadLoading = false;
        state.error = action.payload as string;
      })
      // Assign worker
      .addCase(assignWorkerToOrder.fulfilled, (state, action) => {
        const { orderId } = action.payload;
        const order = state.orders.find(o => o.id === orderId);
        if (order) {
          // The API should return the updated order with assigned_worker object
          // But for immediate UI update, we'll refresh the orders
          order.status = 'assigned';
        }
      })
      // Cancel order
      .addCase(cancelOrder.fulfilled, (state, action) => {
        const orderId = action.payload.orderId;
        const order = state.orders.find(o => o.id === orderId);
        if (order) {
          order.status = 'cancelled';
        }
      })
      // Deassign worker
      .addCase(deassignWorkerFromOrder.fulfilled, (state, action) => {
        const orderId = action.payload.orderId;
        const order = state.orders.find(o => o.id === orderId);
        if (order) {
          order.assigned_worker = undefined;
          order.assigned_worker_id = undefined;
          order.assigned_worker_name = undefined;
          order.status = 'pending';
        }
      })
      // Bulk assign orders
      .addCase(bulkAssignOrders.fulfilled, () => {
        // Refresh will be triggered by the component
      })
      // Bulk cancel orders
      .addCase(bulkCancelOrders.fulfilled, () => {
        // Refresh will be triggered by the component
      })
      
      // Mark item as picked
      .addCase(markItemPicked.fulfilled, (state, action) => {
        const { orderId, itemId } = action.payload;
        const order = state.orders.find(o => o.id === orderId);
        if (order) {
          const item = order.items.find(i => i.id === itemId);
          if (item) {
            item.status = 'picked';
            item.picked_at = new Date().toISOString();          }
        }
      })
      // Complete order
      .addCase(completeOrder.fulfilled, (state, action) => {
        const { orderId } = action.payload;
        const order = state.orders.find(o => o.id === orderId);
        if (order) {
          order.status = 'completed';
          order.completed_at = new Date().toISOString();
          // Mark all items as picked if not already
          order.items.forEach(item => {
            if (item.status !== 'picked') {
              item.status = 'picked';
              item.picked_at = new Date().toISOString();
            }
          });
        }
      })
      // Fetch order by ID
      .addCase(fetchOrderById.fulfilled, (state, action) => {
        state.selectedOrder = action.payload;
      })
      
      // Verify order
      .addCase(verifyOrder.fulfilled, (state, action) => {
        const { orderId } = action.payload;
        const order = state.orders.find(o => o.id === orderId);
        if (order) {
          order.status = 'verified';
          order.verified_at = new Date().toISOString();
          order.verification_notes = action.payload.verification_notes;
          // Update discrepancies
          if (action.payload.discrepancies) {
            order.discrepancies = action.payload.discrepancies;
          }
        }
      });
  }
});

export const { setSelectedOrder, setFilters, clearError, clearUploadResult, resetState } = orderManagementSlice.actions;
export default orderManagementSlice.reducer;
